import React, { useState, useEffect, useRef } from "react";
import {
  useGetConversationQuery,
  useSendMessageMutation,
  useMarkConversationReadMutation,
  useDeleteConversationMutation,
} from "../../../../store/slices/api";
import { SharedButton, SharedLoading } from "../../../shared";
import LetterCard from "./LetterCard";
import { failedMessage } from "../../../shared/serverReason";

const MAX_CHARS = 600;

function LetterThread({ conversationId, individualId, onBack }) {
  const { data: conversation, isLoading } = useGetConversationQuery(
    { conversationId, individualId },
    { skip: !conversationId || !individualId, pollingInterval: 10000 }
  );
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [markRead] = useMarkConversationReadMutation();
  const [deleteConversation, { isLoading: isDeleting }] = useDeleteConversationMutation();

  const [replyContent, setReplyContent] = useState("");
  const [replyMediaUrl, setReplyMediaUrl] = useState(null);
  const [replyMediaType, setReplyMediaType] = useState("text");
  const [sendError, setSendError] = useState(null);
  const [archiveError, setArchiveError] = useState(null);
  const bottomRef = useRef(null);

  // Mark conversation as read when opened
  useEffect(() => {
    if (conversationId && individualId) {
      markRead({ conversationId, individual_id: individualId });
    }
  }, [conversationId, individualId, markRead]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (conversation?.messages?.length) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversation?.messages?.length]);

  const handleSendReply = async () => {
    if (!replyContent.trim() || replyContent.length > MAX_CHARS) return;

    setSendError(null);
    // Only clear the reply once it is really sent. On failure the words stay
    // in the box and the writer is told why (it used to vanish silently).
    try {
      await sendMessage({
        conversation_id: conversationId,
        sender_individual_id: individualId,
        content: replyContent.trim(),
        message_type: replyMediaType,
        media_url: replyMediaUrl,
      }).unwrap();
    } catch (err) {
      setSendError(failedMessage(err, "Your letter wasn't sent. Your words are still here, so you can try again."));
      return;
    }

    setReplyContent("");
    setReplyMediaUrl(null);
    setReplyMediaType("text");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isGif = file.type === "image/gif";

    const reader = new FileReader();
    reader.onload = (event) => {
      if (isGif) {
        // GIFs: store as-is (no resizing)
        setReplyMediaUrl(event.target.result);
        setReplyMediaType("gif");
      } else {
        // Images: resize to 400px max
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          const maxSize = 400;
          if (width > height) {
            if (width > maxSize) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext("2d").drawImage(img, 0, 0, width, height);
          setReplyMediaUrl(canvas.toDataURL("image/jpeg", 0.8));
          setReplyMediaType("image");
        };
        img.src = event.target.result;
      }
    };
    reader.readAsDataURL(file);
  };

  if (isLoading) return <SharedLoading />;
  if (!conversation) return null;

  // Find the other person's name for the header
  const otherParticipant = conversation.participants?.find(
    (p) => p.individual_id !== individualId
  );
  const selfParticipant = conversation.participants?.find(
    (p) => p.individual_id === individualId
  );
  const otherName = otherParticipant?.individual_name || selfParticipant?.individual_name || "Unknown";

  const charCount = replyContent.length;
  const charClass =
    charCount > MAX_CHARS
      ? "char-counter-over"
      : charCount > MAX_CHARS * 0.9
      ? "char-counter-warn"
      : "char-counter-ok";

  return (
    <div className="flex flex-col gap-y-[24px]">
      {/* Back button and subject header */}
      <div className="flex flex-row items-center gap-x-[16px]">
        <div
          className="text-[14px] font-body text-dupontGold cursor-pointer hover:underline"
          onClick={onBack}
        >
          &larr; Back
        </div>
        <div className="flex flex-col flex-1">
          <h2 className="text-[24px] font-display font-semibold text-dupontBlue dark:text-darkText">
            {conversation.subject}
          </h2>
          <span className="text-[14px] font-body text-dupontGray dark:text-darkTextSecondary italic">
            Correspondence with {otherName}
          </span>
        </div>
        <button
          onClick={async () => {
            if (!window.confirm(
              "Are you sure you want to archive this conversation? It will be removed from your inbox, but the letters are not deleted. It comes back to your inbox if a new letter arrives."
            )) return;
            setArchiveError(null);
            try {
              await deleteConversation({
                conversationId,
                individual_id: individualId,
              }).unwrap();
              onBack();
            } catch (err) {
              setArchiveError(failedMessage(err, "That conversation couldn't be archived. Please try again."));
            }
          }}
          disabled={isDeleting}
          className="text-[13px] font-display font-semibold px-[14px] py-[6px] rounded-[8px] border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          {isDeleting ? "Archiving..." : "Archive"}
        </button>
      </div>

      {archiveError && (
        <div role="alert" className="text-[14px] font-body text-red-700 dark:text-red-400">
          {archiveError}
        </div>
      )}

      {/* Message thread */}
      <div className="flex flex-col gap-y-[20px]">
        {conversation.messages?.map((message, idx) => {
          // Check if this is the last message I sent — if so, show "Read" indicator
          const isOwn = message.sender_individual_id === individualId;
          const isLastOwnMessage =
            isOwn &&
            !conversation.messages
              .slice(idx + 1)
              .some((m) => m.sender_individual_id === individualId);

          let showRead = false;
          if (isLastOwnMessage) {
            const otherParticipant = conversation.participants?.find(
              (p) => p.individual_id !== individualId
            );
            if (
              otherParticipant?.last_read_at &&
              message.created_at &&
              new Date(otherParticipant.last_read_at) >=
                new Date(message.created_at)
            ) {
              showRead = true;
            }
          }

          return (
            <React.Fragment key={message.id}>
              <LetterCard
                message={message}
                isOwnMessage={isOwn}
              />
              {showRead && (
                <div className="text-right pr-[8px] -mt-[12px]">
                  <span className="text-[13px] font-body text-dupontGold italic">
                    Read
                  </span>
                </div>
              )}
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply area */}
      <div className="flex flex-col gap-y-[12px] mt-[8px]">
        <div className="correspondence-divider" />
        <h3 className="text-[18px] font-display font-semibold text-dupontBlue dark:text-darkText mt-[8px]">
          Write a Reply
        </h3>

        <textarea
          className="stationery-textarea"
          placeholder="Dear cousin..."
          value={replyContent}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) {
              setReplyContent(e.target.value);
            }
          }}
        />
        <div className={`char-counter ${charClass}`}>
          {charCount}/{MAX_CHARS}
        </div>

        {/* Media preview */}
        {replyMediaUrl && (
          <div className="flex flex-row items-start gap-x-[12px]">
            <div className="letter-media-frame">
              <img src={replyMediaUrl} alt="Attachment" />
            </div>
            <div
              className="text-[13px] text-dupontGold cursor-pointer hover:underline mt-[4px]"
              onClick={() => {
                setReplyMediaUrl(null);
                setReplyMediaType("text");
              }}
            >
              Remove
            </div>
          </div>
        )}

        {/* Action row: attach + send */}
        <div className="flex flex-row items-center justify-between">
          <label className="text-[14px] font-body text-dupontGold cursor-pointer hover:underline">
            Attach Image or GIF
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
          </label>
          <SharedButton
            onClick={handleSendReply}
            disabled={!replyContent.trim() || isSending}
            loadingText={isSending ? "Sending..." : undefined}
          >
            Seal & Send
          </SharedButton>
        </div>
        {sendError && (
          <div role="alert" className="text-[14px] font-body text-red-700 dark:text-red-400">
            {sendError}
          </div>
        )}
      </div>
    </div>
  );
}

export default LetterThread;
